// ============================================================================
// ui/logo-tap-fix-sheet.js — the "Clean up logo" editor (WD 483; renamed 483c; renamed again WD 498)
// ============================================================================
// The buyer taps a piece of their logo: solid -> see-through, see-through -> solid (the pixel logic is
// `logo-tap-fix.js`). Shown over the grey/white CHECKERBOARD the YOUR LOGO thumbnail uses (Ryan 11-01: a solid
// background hides every part of its own colour - Carroll's black mouth on black - and this editor's one job is
// showing what is see-through); the real look on the weight shows after Use this.
// // can both be seen. Undo, Start over, Cancel, Use this. Resolves the edited canvas, or null.
// ============================================================================
import { h } from './dom.js';
import { openModal } from './modal.js';
import { clearRegionAt, fillRegionAt } from '../logo-tap-fix.js';
import { seeThroughChooser } from './see-through-chooser.js';
import { isPhone } from './mobile-launch.js';

const WORK_MAX = 1024;
// the loupe's checks: the stage's own two greys (.wd-tapfix-stage), at the loupe's zoom
const CHECK_A = '#e9edf2', CHECK_B = '#bdc4cd'; // WD 498: the grey 5% darker (Ryan 11-01), as the CSS checks

function toCanvas(drawable, W, H) {
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d', { willReadFrequently: true });
  g.drawImage(drawable, 0, 0, W, H);
  return c;
}

/**
 * @param {object} o
 * @param {HTMLElement} o.mount   the .weight-designer root
 * @param {CanvasImageSource} o.image   the logo as it is now
 * @param {CanvasImageSource|null} o.source   the art before any background was removed (restores a fill);
 *                                            null -> only what this session cleared can come back
 * @param {CanvasImageSource|null} o.original the logo BEFORE any tap edits ("Reset logo" goes back to it, even
 *                                            after an earlier Use this); null -> reset only undoes this visit
 * @param {object|null} o.starts  WD 489: the Solid / See-through starting points, on a logo whose two versions differ
 *                                (the app is unsure): {value, previews: {solid, see, plate}, art: (v) => Promise<image>}.
 *                                null -> no pair (nothing to choose).
 * @param {object|null} o.bg      WD 498: the BACKGROUND pair (Keep / Remove), on a logo with a removable solid field:
 *                                {value, previews: {solid, see, plate}, art: (v) => Promise<image>, starts?}. null -> no
 *                                pair. `bg.starts` (same shape as `starts`) is the INSIDE pair for the removed version -
 *                                shown only while Remove is picked, under the background pair (Ryan's stacked pairs).
 * @returns {Promise<{canvas: HTMLCanvasElement, atOriginal: boolean, pick?: boolean, bg?: boolean}|null>}  `pick` /
 *          `bg` only when the buyer chose the OTHER starting point / the other background answer.
 */
export async function openTapFix({ mount, image, source, original = null, starts: startsIn = null, bg = null }) {
  // WD 498: the inside pair is either the logo's own (a client-keyed logo) or the one the background removal leaves.
  const starts = startsIn || (bg && bg.starts) || null;
  const startsFromBg = !startsIn && !!(bg && bg.starts);
  const iw = image.naturalWidth || image.width, ih = image.naturalHeight || image.height;
  const s = Math.min(1, WORK_MAX / Math.max(iw, ih));
  // `let`: a background pick whose art is a different shape (the removed version is trimmed) re-frames the editor.
  let W = Math.max(1, Math.round(iw * s)), H = Math.max(1, Math.round(ih * s));
  const view = toCanvas(image, W, H);
  view.className = 'wd-tapfix-canvas';
  view.style.aspectRatio = `${W} / ${H}`;
  const g = view.getContext('2d', { willReadFrequently: true });
  const first = g.getImageData(0, 0, W, H);
  let src = source ? toCanvas(source, W, H).getContext('2d', { willReadFrequently: true }).getImageData(0, 0, W, H) : first;
  // "Reset logo" (Ryan 10-31: "if you make some edits, click use this, then go back in and click start over? ... a
  // 'reset'") returns to the logo before ANY tap edit, not just to how this visit opened. A step like any other, so
  // Undo reverses it.
  const orig = original ? toCanvas(original, W, H).getContext('2d', { willReadFrequently: true }).getImageData(0, 0, W, H).data : null;
  /* WD 489: every step is a SNAPSHOT - the pixels, what Reset goes back to (`base`), which starting point it grew from
     (`pick`) and whether it IS that base. A Solid / See-through pick changes the base as well as the pixels, so Undo
     has to restore all four together; recomputing them after a pop is how a bare pixel stack would get it wrong. */
  const step0 = { data: first.data, base: orig, pick: starts ? !!starts.value : null, bg: bg ? !!bg.value : null, atOriginal: !original }; // opened unedited -> at the original
  let history = [step0];
  let now = step0;
  let cur = new ImageData(new Uint8ClampedArray(first.data), W, H);


  const stage = h('div', { class: 'wd-tapfix-stage' }, view);
  const hint = h('p', { class: 'wd-tapfix-hint', 'aria-live': 'polite' },
    'Click the logo to change parts from visible to see-through and back again.'); // Ryan's words, 11-01
  /* WD 485 (Ryan 11-01): Undo and Reset - icon + word, ~2x the old square (a Black | White background toggle sat
     beside them for a day as icon-only squares with captions; the checkerboard replaced it and freed the row). */
  const UNDO_ICON = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 14L4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11"/></svg>';
  const RESET_ICON = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 2.64-6.36L3 8"/><path d="M3 3v5h5"/></svg>';
  // the house flat-glass buttons (as "Reset colors"), not the generic ghost button (Ryan 10-31: "isn't on brand")
  const TOOL = 'wd-reset-btn wd-flat-glass wd-flat-glass--flush wd-tapfix-tool';
  const iconBtn = (icon, label, title, fn) => {
    const btn = h('button', { type: 'button', class: TOOL, title, onClick: fn });
    btn.innerHTML = icon;
    btn.appendChild(h('span', {}, label));
    return btn;
  };
  const undoBtn = iconBtn(UNDO_ICON, 'Undo', 'Undo', () => undo());
  const resetBtn = iconBtn(RESET_ICON, 'Reset', 'Reset logo', () => reset());
  /* ── WD 489: THE TWO STARTING POINTS (Ryan 11-01) ─────────────────────────────────────────────────────────────
     The Solid / See-through pair left the Add Logo tab and lives here, above the big logo, on a logo whose two versions
     differ. The same cards as the old chooser (`seeThroughChooser`), smaller. Picking one replaces the logo below with
     that version, so any tapped pieces go - said in one line, and Undo brings them back (Ryan: "yes"). */
  const START_NOTE = 'Start from one of these.'; // WD 498: the tapping line lives under the big logo only (Ryan)
  let pair = null, note = null, picking = 0;
  if (starts) {
    pair = seeThroughChooser({ label: 'Logo cutouts', caption: false, value: !!starts.value, ariaLabel: 'Start from the solid or the see-through version of your logo', onChange: (v) => pickStart(v) });
    pair.setPreviews(starts.previews ? { ...starts.previews, plate: '' } : null); // WD 498: checkerboard disc (CSS), not a plate colour
    pair.el.hidden = false;
    pair.el.classList.add('wd-tapfix-starts');
    note = h('p', { class: 'wd-tapfix-note', 'aria-live': 'polite' }, START_NOTE);
  }
  /* ── WD 498: THE BACKGROUND PAIR (Ryan 11-01) ──────────────────────────────────────────────────────────────────
     The "Remove the background color" switch left the Add Logo tab and lives here as two cards, Keep / Remove, ABOVE
     the cutouts pair (his "stacked pairs"). Picking one swaps the logo below for that version, exactly like a starting
     point. The removed version is trimmed to the logo, so it may be a different shape: then the editor re-frames and
     the tapped history starts over (said in the note). Each pair carries a small heading only when both are shown. */
  let bgPair = null;
  const BG_NOTE = "Keep or remove your logo's background.";
  const INSIDE_NOTE = 'Keep or remove the spaces inside your logo.';
  if (bg) {
    bgPair = seeThroughChooser({ label: 'Background', caption: false, value: !!bg.value, names: ['Keep', 'Remove'],
      says: ['the solid background color stays', 'the weight shows where the background color was'],
      ariaLabel: 'Keep or remove the solid background color of your logo', onChange: (v) => pickBg(v) });
    bgPair.setPreviews(bg.previews ? { ...bg.previews, plate: '' } : null); // WD 498: checkerboard disc
    bgPair.el.hidden = false;
    bgPair.el.classList.add('wd-tapfix-starts');
    // the first question on screen is the background one, so the note speaks to it (the inside pair waits on Remove)
    if (!note) note = h('p', { class: 'wd-tapfix-note', 'aria-live': 'polite' }, '');
    note.textContent = (bg.value && bg.starts) ? INSIDE_NOTE : BG_NOTE; // reopened on a removed background: both pairs are up
  }
  const both = !!(bgPair && pair);
  // WD 498: an inside pair that comes from the background removal only means something while Remove is picked.
  /* It SLIDES open and shut (Ryan 11-01: "make them slide into position, not abruptly snap"): the heading and the pair
     sit in a 0fr -> 1fr grid-rows wrapper, the app's own recipe for a reveal (the slow-upload line, the Advanced block). */
  /* WD 499 (Ryan 11-01): the caption speaks to the NEWEST question - the background until Remove is picked, then the
     inside pair that just slid in. Set only when that changes, so a warning written by the pick that caused it (set
     after `show`) is not overwritten. */
  let insideShown = null;
  const syncInside = () => {
    if (!startsFromBg || !pair || !insideWrap) return;
    const open = now.bg === true;
    if (insideShown !== null && open !== insideShown && note) note.textContent = open ? INSIDE_NOTE : BG_NOTE;
    insideShown = open;
    insideWrap.classList.toggle('is-open', open);
    body.classList.toggle('wd-tapfix--two', now.bg === true); // two pairs on screen: the big logo gives them the room
  };
  let insideHead = null, insideWrap = null;
  const head = (t) => h('p', { class: 'wd-tapfix-pairhead' }, t);
  const top = [
    ...(bgPair ? [...(both ? [head('Background')] : []), bgPair.el] : []),
    ...(pair ? (startsFromBg
      ? [insideWrap = h('div', { class: 'wd-tapfix-inside' + (step0.bg === true ? ' is-open' : '') },
        h('div', { class: 'wd-tapfix-inside__in' }, ...(both ? [insideHead = head('Inside the logo')] : []), pair.el))]
      : [...(both ? [insideHead = head('Inside the logo')] : []), pair.el]) : []),
    ...(note ? [note] : []),
  ];
  const body = h('div', { class: 'wd-tapfix' + ((starts || bg) ? ' wd-tapfix--starts' : '') }, ...top, stage, hint,
    h('div', { class: 'wd-tapfix-tools' }, undoBtn, resetBtn));

  const paint = () => {
    g.putImageData(cur, 0, 0);
    undoBtn.disabled = history.length < 2;
    resetBtn.disabled = now.atOriginal;
    if (pair && now.pick !== null) pair.set(now.pick); // silent: an Undo across a pick moves the cards back too
    if (bgPair && now.bg !== null) bgPair.set(now.bg);
    syncInside();
  };
  const show = (step) => { now = step; cur = new ImageData(new Uint8ClampedArray(step.data), W, H); paint(); };
  const push = (data, msg, isOriginal = false, pieces = now.pieces || []) => {
    const step = { ...now, data, atOriginal: isOriginal, pieces };
    history.push(step);
    hint.textContent = msg;
    show(step);
  };
  const undo = () => {
    if (history.length < 2) return;
    const was = history.pop();
    hint.textContent = 'Undone.';
    if (note && was.pick !== history[history.length - 1].pick) note.textContent = startsFromBg ? INSIDE_NOTE : START_NOTE;
    show(history[history.length - 1]);
  };
  const reset = () => {
    if (now.atOriginal) return;
    push(new Uint8ClampedArray(now.base || history[0].data), 'Your logo is back to how it was uploaded. Undo brings your edits back.', true, []);
  };
  /* A pick is a step: the pixels become that version, and so does what Reset goes back to. "Tapped changes" = this
     step is not its own base (a tap since the last pick or open), the only time there is anything to clear. */
  async function pickStart(v) {
    if (!starts || v === now.pick) return;
    const job = ++picking;
    let art = null;
    try { art = await starts.art(v); } catch {}
    if (job !== picking) return;
    if (!art) { pair.set(now.pick); note.textContent = "We couldn't make that version of your logo, so nothing changed."; return; }
    const aw = art.naturalWidth || art.width, ah = art.naturalHeight || art.height;
    /* The two versions are cut from one frame, so they line up; one trimmed to a different shape would not, and
       stretching it onto this canvas would put every later tap in the wrong place. Checked, not assumed. */
    if (Math.abs(aw / ah - W / H) > 0.01) { pair.set(now.pick); note.textContent = "That version doesn't line up with this one, so nothing changed."; return; }
    const data = toCanvas(art, W, H).getContext('2d', { willReadFrequently: true }).getImageData(0, 0, W, H).data;
    const hadTaps = !now.atOriginal;
    const step = { ...now, data, base: data, pick: v, atOriginal: true, pieces: [] }; // WD 498: keeps the background answer; new pixels, no cleared pieces
    history.push(step);
    // WD 498 (Ryan 11-01: "Switched to solid" is not necessary - they can see what they clicked): only a WARNING is said
    if (hadTaps) note.textContent = 'Your tapped changes were cleared; Undo brings them back.';
    hint.textContent = 'Click the logo to change parts from visible to see-through and back again.';
    show(step);
  }

  /* WD 498: a background pick, built like `pickStart`. Same shape -> one more step (Undo reaches back across it). A
     different shape (the removed version is trimmed to the logo) -> the editor re-frames to it and starts its history
     there, because a tap history cannot be carried onto a canvas of another size. */
  let bgJob = 0;
  async function pickBg(v) {
    if (!bg || v === now.bg) return;
    const job = ++bgJob;
    let art = null;
    try { art = (v && startsFromBg && now.pick !== null) ? await bg.starts.art(now.pick) : await bg.art(v); } catch {}
    if (job !== bgJob) return;
    if (!art) { bgPair.set(now.bg); note.textContent = "We couldn't make that version of your logo, so nothing changed."; return; }
    const aw = art.naturalWidth || art.width, ah = art.naturalHeight || art.height;
    const hadTaps = !now.atOriginal || history.length > 1;
    if (Math.abs(aw / ah - W / H) <= 0.01) {
      const data = toCanvas(art, W, H).getContext('2d', { willReadFrequently: true }).getImageData(0, 0, W, H).data;
      const step = { ...now, data, base: data, bg: v, atOriginal: true, pieces: [] };
      history.push(step);
      const warn = !now.atOriginal;
      show(step);
      if (warn) note.textContent = 'Your tapped changes were cleared; Undo brings them back.'; // WD 498: no narration (after show: wins over the caption)
      return;
    }
    const k = Math.min(1, WORK_MAX / Math.max(aw, ah));
    W = Math.max(1, Math.round(aw * k)); H = Math.max(1, Math.round(ah * k));
    view.width = W; view.height = H;
    view.style.aspectRatio = `${W} / ${H}`;
    const data = toCanvas(art, W, H).getContext('2d', { willReadFrequently: true }).getImageData(0, 0, W, H).data;
    src = new ImageData(new Uint8ClampedArray(data), W, H); // a fill can only put back this version's own pixels now
    const step = { data, base: data, pick: now.pick, bg: v, atOriginal: true, pieces: [] };
    history = [step];
    show(step);
    if (hadTaps) note.textContent = 'Your tapped changes were cleared.'; // WD 498: no narration (after show: wins over the caption)
  }

  // client point -> working-copy pixel. The canvas keeps its aspect inside the stage (object-fit: contain), so map
  // through the drawn box. Also returns the on-screen scale (screen px per working px) the loupe needs.
  const toImage = (cx, cy) => {
    const r = view.getBoundingClientRect();
    const k = Math.min(r.width / W, r.height / H);
    const ox = r.left + (r.width - W * k) / 2, oy = r.top + (r.height - H * k) / 2;
    return { x: (cx - ox) / k, y: (cy - oy) / k, k };
  };
  const inside = (p) => p.x >= 0 && p.y >= 0 && p.x < W && p.y < H;
  /* ⛔ WD 499 — A PART YOU CLEARED COMES BACK WHEN YOU CLICK IT AGAIN (Ryan 11-01: "I can click on the big yellow 'M' and
     it disappears, but clicking again in that same area and nothing re-emerges… This inconsistency is very annoying").
     The refill (`fillRegionAt`) can only bring back an area the logo ENCLOSES - it refuses anything open to the
     outside, so the M, and every white letter of a logo whose background is gone, could be cleared and never
     un-cleared; the D worked only because its counter is enclosed. Each clear now remembers exactly which pixels it
     changed and what they were (`pieces`, carried on the history step so Undo and Reset agree); a click on a
     see-through pixel inside a remembered piece puts THAT piece back, newest first. The enclosed-area refill stays as
     the fallback for areas that were see-through when the editor opened. */
  const applyAt = (x, y) => {
    const k0 = Math.floor(y) * W + Math.floor(x);
    const opaque = cur.data[k0 * 4 + 3] >= 128;
    if (!opaque) {
      const pieces = now.pieces || [];
      for (let i = pieces.length - 1; i >= 0; i--) {
        const pc = pieces[i];
        if (!pc.mask[k0]) continue;
        const out = new Uint8ClampedArray(cur.data);
        for (let j = 0; j < pc.idx.length; j++) {
          const q = pc.idx[j] * 4, r = j * 4;
          out[q] = pc.px[r]; out[q + 1] = pc.px[r + 1]; out[q + 2] = pc.px[r + 2]; out[q + 3] = pc.px[r + 3];
        }
        push(out, 'Brought back. Tap it again to make it see-through.', false, pieces.filter((_, j) => j !== i));
        return;
      }
    }
    const res = opaque ? clearRegionAt(cur, x, y) : fillRegionAt(cur, src, x, y);
    if (!res) {
      hint.textContent = opaque ? 'Nothing to change there.'
        : "That area is open to the outside of your logo, so it can't be filled. Try a spot that's surrounded by your logo.";
      return;
    }
    if (opaque) {
      // remember the piece: every pixel this clear changed, and what it was
      const a = cur.data, b = res.data, idx = [];
      const mask = new Uint8Array(W * H);
      for (let k = 0; k < W * H; k++) {
        const q = k * 4;
        if (a[q + 3] !== b[q + 3] || a[q] !== b[q] || a[q + 1] !== b[q + 1] || a[q + 2] !== b[q + 2]) { idx.push(k); mask[k] = 1; }
      }
      const px = new Uint8ClampedArray(idx.length * 4);
      idx.forEach((k, j) => { px.set(a.subarray(k * 4, k * 4 + 4), j * 4); });
      push(res.data, 'Made see-through. Tap it again to bring it back.', false, [...(now.pieces || []), { mask, idx: Int32Array.from(idx), px }]);
      return;
    }
    push(res.data, 'Filled in. Tap it again to make it see-through.');
  };

  /* ── THE MAGNIFIER (WD 485, Ryan 10-31: "a circle to zoom in on a section with the crosshair in the middle ... so
     you can click on details"). A ~3x loupe of what is on screen, crosshair at its centre, and the centre is where a
     tap acts. Desktop: it sits ON the pointer and follows it; a click acts at the centre. Touch: a finger hides what
     it is on, so the loupe sits ABOVE the finger showing what is under it; drag to aim, lift to apply.
     ⚠ Hosted in the scrim, not the dialog: the dialog body scrolls (overflow-y: auto) and would clip a loupe near
     its edge, and the scrim is the viewport-sized fixed layer, so its coordinates are client coordinates. */
  const ZOOM = 3;
  const loupeCv = document.createElement('canvas');
  loupeCv.className = 'wd-tapfix-loupe__cv';
  const loupe = h('div', { class: 'wd-tapfix-loupe', hidden: true, 'aria-hidden': 'true' }, loupeCv);
  const lg = loupeCv.getContext('2d');
  const showLoupe = (aim, cx, cy, touch) => {
    if (!loupe.isConnected) (view.closest('.wd-modal-scrim') || document.body).appendChild(loupe);
    loupe.hidden = false;
    const host = loupe.parentElement.getBoundingClientRect();
    const D = loupe.offsetWidth || 120;
    const dpr = Math.min(3, window.devicePixelRatio || 1), Dp = Math.round(D * dpr);
    if (loupeCv.width !== Dp) { loupeCv.width = Dp; loupeCv.height = Dp; }
    // where the circle goes: on the pointer (mouse); above the finger, or beside it when there is no room above
    let lx = cx, ly = cy;
    if (touch) {
      const lift = D / 2 + 34;
      if (cy - lift - D / 2 >= host.top + 8) ly = cy - lift;
      else lx = cx + (cx + lift + D / 2 <= host.right - 8 ? lift : -lift);
      lx = Math.max(host.left + D / 2 + 8, Math.min(host.right - D / 2 - 8, lx));
    }
    loupe.style.left = `${lx - host.left - D / 2}px`;
    loupe.style.top = `${ly - host.top - D / 2}px`;
    // the checks, then the logo's pixels around the aim point at ZOOM x their on-screen size (crisp, not smoothed)
    const sq = Math.round(6 * ZOOM * dpr);
    for (let yy = 0; yy < Dp; yy += sq) for (let xx = 0; xx < Dp; xx += sq) {
      lg.fillStyle = ((xx / sq + yy / sq) & 1) ? CHECK_B : CHECK_A;
      lg.fillRect(xx, yy, sq, sq);
    }
    const span = D / (ZOOM * aim.k); // working px across the circle
    const sx = aim.x - span / 2, sy = aim.y - span / 2, f = Dp / span;
    const ax = Math.max(0, sx), ay = Math.max(0, sy), bx = Math.min(W, sx + span), by = Math.min(H, sy + span);
    lg.imageSmoothingEnabled = false;
    if (bx > ax && by > ay) lg.drawImage(view, ax, ay, bx - ax, by - ay, (ax - sx) * f, (ay - sy) * f, (bx - ax) * f, (by - ay) * f);
  };
  const hideLoupe = () => { loupe.hidden = true; };

  let touchId = null, touchAim = null, hintBeforeAim = null;
  view.addEventListener('pointerdown', (e) => {
    const aim = toImage(e.clientX, e.clientY);
    if (e.pointerType === 'mouse') {
      if (e.button !== 0 || !inside(aim)) return;
      applyAt(aim.x, aim.y);
      showLoupe(aim, e.clientX, e.clientY, false); // the edit is in the circle at once
      return;
    }
    // touch / pen: aim first, act on lift
    if (touchId !== null) return; // a second finger does nothing
    e.preventDefault();
    touchId = e.pointerId;
    try { view.setPointerCapture(e.pointerId); } catch {}
    touchAim = aim;
    hintBeforeAim = hint.textContent;
    hint.textContent = 'Slide to aim the crosshair, then lift your finger to change that part.';
    showLoupe(aim, e.clientX, e.clientY, true);
  });
  view.addEventListener('pointermove', (e) => {
    if (e.pointerType === 'mouse') {
      const aim = toImage(e.clientX, e.clientY);
      if (inside(aim)) showLoupe(aim, e.clientX, e.clientY, false); else hideLoupe();
      return;
    }
    if (e.pointerId !== touchId) return;
    touchAim = toImage(e.clientX, e.clientY);
    showLoupe(touchAim, e.clientX, e.clientY, true);
  });
  const endTouch = (e, apply) => {
    if (e.pointerId !== touchId) return;
    touchId = null;
    hideLoupe();
    if (hintBeforeAim !== null) { hint.textContent = hintBeforeAim; hintBeforeAim = null; }
    if (apply && touchAim && inside(touchAim)) applyAt(touchAim.x, touchAim.y);
    touchAim = null;
  };
  view.addEventListener('pointerup', (e) => endTouch(e, true));
  view.addEventListener('pointercancel', (e) => endTouch(e, false));
  view.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') hideLoupe(); });

  paint();
  const choice = await openModal({
    mount,
    title: 'Clean up logo', // WD 498 (Ryan 11-01): covers the background as well as the cutouts
    body,
    // the app's grey glass dialog (pill actions, centred) + this editor's sizing; WD 503 (Ryan 11-02): on a phone it is a
    // full-height sheet that slides up like Finalize, so the logo gets the whole width of the screen
    variant: isPhone() ? 'glass tapfix sheet' : 'glass tapfix',
    actions: [{ id: 'cancel', label: 'Cancel', variant: 'ghost' }, { id: 'use', label: 'Use this', variant: 'primary' }],
  });
  loupe.remove();
  // WD 498: a re-framed background pick starts the history over, so a changed background answer counts as a change too
  const bgChanged = !!bg && now.bg !== !!bg.value;
  if (choice !== 'use' || (history.length < 2 && !bgChanged)) return null;
  g.putImageData(cur, 0, 0);
  const res = { canvas: view, atOriginal: now.atOriginal };
  if (starts && now.pick !== !!starts.value) res.pick = now.pick;
  if (bg && now.bg !== !!bg.value) res.bg = now.bg;
  return res;
}
